import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaArrowRight } from 'react-icons/fa';
import config from '../../../../config/config';
import { useShopCategories } from '../../../shop/hooks/useShopCategories';
import type { ShopCategory } from '../../../shop/types';
import './ShopProducts.css';

const PLACEHOLDER = '/images/no-image.webp';

interface CategoryCardProps {
  category: ShopCategory;
  index: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, index }) => {
  const [imageSrc, setImageSrc] = useState<string>(category.imageUrl || PLACEHOLDER);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: Math.min(index, 7) * 0.1 }}
      whileHover={{ y: -5 }}
    >
      <Link
        to={`/boutique/categorie/${category.slug}`}
        className="shop-category-card"
        onClick={() => window.scrollTo(0, 0)}
      >
        <div className="shop-category-card__media">
          <img
            src={imageSrc}
            alt={category.name}
            loading="lazy"
            onError={() => setImageSrc(PLACEHOLDER)}
          />
        </div>
        <div className="shop-category-card__body">
          <h3 className="shop-category-card__title">{category.name}</h3>
          <span className="shop-category-card__count">{category.productCount}</span>
        </div>
      </Link>
    </motion.div>
  );
};

const CategoryCardSkeleton: React.FC = () => (
  <div className="shop-category-skeleton" aria-hidden="true">
    <div className="shop-category-skeleton__media" />
    <div className="shop-category-skeleton__line" />
  </div>
);

/**
 * Category showcase on the home page — links into the catalog pre-filtered
 * by category rather than listing individual products.
 *
 * Degradation contract: this section must never break or delay the home page.
 * On error — or when there are simply no categories — it renders nothing at
 * all rather than showing an error state on the site's most important page.
 */
const ShopProducts: React.FC = () => {
  const { t } = useTranslation();
  const { data: categories, isPending, isError } = useShopCategories();

  if (isError) return null;

  if (isPending) {
    return (
      <section className="shop-products-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {t('home.shop.title', 'Notre boutique')}
            </h2>
          </div>
          <div className="shop-categories-grid">
            {Array.from({ length: config.shop.homeCategoriesLimit }).map((_, index) => (
              <CategoryCardSkeleton key={index} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!categories || categories.length === 0) return null;

  const topCategories = [...categories]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .slice(0, config.shop.homeCategoriesLimit);

  return (
    <section className="shop-products-section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">{t('home.shop.title', 'Notre boutique')}</h2>
          <p className="section-subtitle">
            {t(
              'home.shop.subtitle',
              'Équipements et fournitures disponibles à la commande, payables à la livraison'
            )}
          </p>
        </motion.div>

        <div className="shop-categories-grid">
          {topCategories.map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} />
          ))}
        </div>

        <motion.div
          className="shop-products-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <Link to="/boutique" onClick={() => window.scrollTo(0, 0)}>
            {t('home.shop.cta', 'Voir toute la boutique')}
            <FaArrowRight aria-hidden="true" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ShopProducts;
